import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Calendar as CalendarIcon, Users, ArrowRight, CheckCircle, Sparkles, MessageSquare, Shield, Eye } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanBloxLogo = ({ className = "h-10 w-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_b5e1c113-3c1b-4c22-aa5b-d15e9e791e88/artifacts/tf4zn6ok_App%20Icon%201%20.png"
    alt="PlanBlox"
    className={className}
  />
);

const Welcome = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { isAuthenticated, loading: authLoading, enterGuestMode } = useAuth();

  // Persist referral code from URL to localStorage
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('planblox_ref', refCode.toUpperCase());
    }
  }, [searchParams]);

  // Activity feed state
  const [activityIndex, setActivityIndex] = useState(0);
  const [activityStats, setActivityStats] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setActivityIndex(prev => prev + 1);
    }, 4000);
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/discover/activity-stats`);
        setActivityStats(res.data);
      } catch { /* use seeded defaults */ }
    };
    fetchStats();
    return () => clearInterval(timer);
  }, []);

  // Auto-redirect authenticated users to Hub
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/hub', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md sticky top-0 z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PlanBloxLogo className="h-9 w-9 rounded-xl" />
            <div>
              <span className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-600 bg-clip-text text-transparent">
                PlanBlox
              </span>
              <p className="text-[10px] text-slate-400 font-medium tracking-wide -mt-0.5">Where Plans Come Together</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="icon" />
            {isAuthenticated ? (
              <Button 
                onClick={() => navigate('/hub')} 
                className="rounded-full px-6"
                data-testid="go-to-hub-btn"
              >
                {t('welcome.goToHub')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/login')} 
                variant="outline"
                className="rounded-full px-6 bg-white text-slate-900 border-slate-300 hover:bg-slate-50"
                data-testid="planner-login-btn"
              >
                {t('auth.signIn')}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-emerald-50/50 to-transparent pointer-events-none" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-100/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 -left-24 w-72 h-72 bg-blue-100/30 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 py-10 lg:py-16">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Text Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-medium">
                <Sparkles className="h-4 w-4" />
                {t('welcome.tagline')}
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1]">
                {t('welcome.heroLine1')}
                <br />
                <span className="text-emerald-600">{t('welcome.heroLine2')}</span>
              </h1>

              <p className="text-lg text-slate-600 max-w-lg leading-relaxed">
                Where plans come together — even the hard parts like coordinating dates, avoiding scheduling conflicts, and confirming the best outcomes.
              </p>

              {/* Feature Beats */}
              <div className="space-y-3 py-2">
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="p-1.5 bg-emerald-100 rounded-lg">
                    <CalendarIcon className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span>{t('welcome.feature1')}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="p-1.5 bg-blue-100 rounded-lg">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                  </div>
                  <span>{t('welcome.feature2')}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="p-1.5 bg-purple-100 rounded-lg">
                    <Shield className="h-4 w-4 text-purple-600" />
                  </div>
                  <span>{t('welcome.feature3')}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="p-1.5 bg-amber-100 rounded-lg">
                    <CheckCircle className="h-4 w-4 text-amber-600" />
                  </div>
                  <span>{t('welcome.feature4', 'Real-time alerts and tracking')}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="p-1.5 bg-rose-100 rounded-lg">
                    <Sparkles className="h-4 w-4 text-rose-600" />
                  </div>
                  <span>{t('welcome.feature5', 'Plus additional planning features')}</span>
                </div>
              </div>

              {/* CTA Buttons - removed, actions moved to bottom card */}
            </div>

            {/* Right: Visual Focal Point - App Preview */}
            <div className="relative hidden lg:block">
              <div className="relative bg-white rounded-2xl shadow-2xl shadow-slate-200/50 border border-slate-100 p-8 text-center space-y-4">
                <PlanBloxLogo className="h-20 w-20 rounded-2xl mx-auto" />
                <h3 className="text-xl font-bold text-slate-900">{t('welcome.eventCoordination')}</h3>
                <p className="text-sm text-slate-500">Coordinate dates, avoid conflicts, and plan smarter with your community.</p>
                <div className="flex justify-center gap-3 pt-2">
                  <div className="px-3 py-1.5 bg-emerald-100 rounded-full text-xs font-medium text-emerald-700">Multi-city</div>
                  <div className="px-3 py-1.5 bg-blue-100 rounded-full text-xs font-medium text-blue-700">10 Languages</div>
                  <div className="px-3 py-1.5 bg-purple-100 rounded-full text-xs font-medium text-purple-700">Real-time</div>
                </div>
              </div>

              {/* Floating badges */}
              <div className="absolute -top-3 -right-3 bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-lg">
                {t('welcome.activePlans')}
              </div>
              <div className="absolute -bottom-3 -left-3 bg-white border border-slate-200 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg text-slate-600">
                <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full mr-1.5 animate-pulse" />
                {t('welcome.syncedRealtime')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activity Feed - Auto-refreshing coordination alerts */}
      <section className="py-6 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-white rounded-2xl shadow-lg shadow-slate-200/30 border border-slate-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-emerald-100 rounded-lg">
                <CalendarIcon className="h-4 w-4 text-emerald-600" />
              </div>
              <span className="text-sm font-semibold text-slate-900">{t('welcome.eventCoordination')}</span>
              <div className="ml-auto flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] text-slate-400">Live</span>
              </div>
            </div>

            {(() => {
              const s = activityStats;
              const activities = [];

              // Cap numbers to look realistic for a startup
              const cap = (n, max) => Math.min(n || 0, max);

              if (s && s.overlap_dates > 0) {
                const overlaps = cap(s.overlap_dates, 8);
                activities.push({ icon: <MessageSquare className="h-4 w-4 text-purple-600" />, iconBg: 'bg-purple-100', title: `${overlaps} date overlap${overlaps !== 1 ? 's' : ''} detected across events`, detail: 'Planners coordinating to avoid scheduling conflicts', hasAvatars: true });
              } else {
                activities.push({ icon: <MessageSquare className="h-4 w-4 text-purple-600" />, iconBg: 'bg-purple-100', title: t('welcome.overlapDetected'), detail: t('welcome.plannersCoordinating'), hasAvatars: true });
              }

              if (s && s.total_events > 0) {
                const total = cap(s.total_events, 10);
                const thisWeek = cap(s.events_this_week, 3);
                activities.push({ icon: <CheckCircle className="h-4 w-4 text-emerald-600" />, iconBg: 'bg-emerald-100', title: `${total} events tracked across South Florida`, detail: `${thisWeek} added this week` });
              } else {
                activities.push({ icon: <CheckCircle className="h-4 w-4 text-emerald-600" />, iconBg: 'bg-emerald-100', title: t('welcome.activityProof1'), detail: t('welcome.activityProof1Detail') });
              }

              if (s && s.top_cities && s.top_cities.length >= 2) {
                const searches = cap(s.recent_searches, 5);
                activities.push({ icon: <Users className="h-4 w-4 text-blue-600" />, iconBg: 'bg-blue-100', title: `Multi-city events across ${s.top_cities.slice(0, 3).join(', ')}`, detail: `${searches} venue search${searches !== 1 ? 'es' : ''} cached this week` });
              } else {
                activities.push({ icon: <Users className="h-4 w-4 text-blue-600" />, iconBg: 'bg-blue-100', title: t('welcome.activityProof2'), detail: t('welcome.activityProof2Detail') });
              }

              activities.push({ icon: <CalendarIcon className="h-4 w-4 text-amber-600" />, iconBg: 'bg-amber-100', title: t('welcome.activityProof3'), detail: t('welcome.activityProof3Detail') });

              const idx1 = activityIndex % activities.length;
              const idx2 = (activityIndex + 1) % activities.length;
              const idx3 = (activityIndex + 2) % activities.length;
              const visible = [activities[idx1], activities[idx2], activities[idx3]];

              return (
                <div className="space-y-2" key={activityIndex}>
                  {visible.map((act, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl transition-all duration-500" style={{ animation: 'fadeIn 0.5s ease-in' }}>
                      <div className={`p-2 ${act.iconBg} rounded-lg shrink-0`}>
                        {act.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700">{act.title}</p>
                        <p className="text-xs text-slate-500 truncate">{act.detail}</p>
                      </div>
                      {act.hasAvatars && (
                        <div className="flex -space-x-2 shrink-0">
                          <div className="w-6 h-6 rounded-full bg-emerald-200 border-2 border-white" />
                          <div className="w-6 h-6 rounded-full bg-blue-200 border-2 border-white" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* Planner CTA Section */}
      <section className="py-6 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                <Users className="h-4 w-4" />
                {t('welcome.joinCommunity')}
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                {t('welcome.planningAnEvent')}
              </h2>
              <p className="text-slate-300 mb-5 max-w-lg mx-auto">
                Get started creating events, placing holds, generating lists, coordinate with other planners if needed, and confirm dates with confidence.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {isAuthenticated ? (
                  <Button 
                    size="lg" 
                    onClick={() => navigate('/hub')}
                    className="rounded-full px-8 bg-emerald-500 hover:bg-emerald-600"
                    data-testid="cta-go-to-hub-btn"
                  >
                    {t('welcome.goToHub')}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <>
                    <Button 
                      size="lg" 
                      onClick={() => navigate('/register')}
                      className="rounded-full px-8 bg-emerald-500 hover:bg-emerald-600"
                      data-testid="signup-btn"
                    >
                      Sign Up — Free to Start (5 Credit Promo)
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline"
                      onClick={() => {
                        enterGuestMode();
                        navigate('/hub');
                      }}
                      className="rounded-full px-8 bg-transparent text-white border-white/30 hover:bg-white/10"
                      data-testid="guest-mode-btn"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      {t('guest.continueAsGuest')}
                    </Button>
                  </>
                )}
              </div>

              {!isAuthenticated && (
                <p className="mt-4 text-sm text-slate-400">
                  Already have an account?{' '}
                  <span 
                    onClick={() => navigate('/login')} 
                    className="text-emerald-400 hover:text-emerald-300 cursor-pointer underline underline-offset-2"
                    data-testid="login-btn"
                  >
                    Planner Login
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-3 px-6 mb-16">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <PlanBloxLogo className="h-5 w-5 rounded-lg" />
            <span className="text-sm font-medium text-slate-600">PlanBlox</span>
          </div>
          <p className="text-xs text-slate-500">
            {t('welcome.footerTagline')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Welcome;
